import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Gift, Pen, Smartphone, Package, CheckCircle,
  ArrowRight, Percent, ShoppingCart, Star
} from "lucide-react";
import { WA_LINK } from "@/Layout";

const PEN_IMAGES = {
  Start: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/23eeda59e4b6716b81902356d7ad31d605f63a57a12df30faced2fd506cd0ded.png",
  Plus: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/a5defb491e7193c8ac5a2c9179b045961fda09e83d39f27f1a945c30c451cca2.png",
  Rooftop: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/1a3b8ef3b70d95cc117df0f81b770ffeb73da4315d4096d6f0f38d33c1897754.png",
  "Virtual Tour 360": "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/bd39066b9f3bce302c8ab913729500c56d0bafbd82fd91dce6e29efc0e4cd881.png",
};

const ALL_PENS = "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/1af9598c3ab41814b03fadda5eb969e204577a2e529233163652411316dad110.png";
const PHONE_STAND = "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/b71cf704d84e79a2e485dfbd389f625f88f2d81657315904b856aac6606f8373.png";
const BUNDLE_IMG = "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/c09b4e3031a7b7a2e5f5938091d9040decdfd59d87e1fc36e253ac076d6f444f.png";

const PEN_COLORS = {
  Start: { name: "Blu", hex: "#3B82F6" },
  Plus: { name: "Rossa", hex: "#EF4444" },
  Rooftop: { name: "Ambra", hex: "#F59E0B" },
  "Virtual Tour 360": { name: "Verde", hex: "#0D9488" },
};

export const CrossSellingGift = ({ pacchetto = "Plus" }) => {
  const penColor = PEN_COLORS[pacchetto] || PEN_COLORS.Plus;
  const penImage = PEN_IMAGES[pacchetto] || PEN_IMAGES.Plus;

  return (
    <section className="py-14 bg-gradient-to-b from-gray-50 to-white" data-testid="gift-pen-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 mb-3 rounded-full px-4 py-1">
            <Gift className="w-4 h-4 mr-2" />
            Regalo incluso nel pacchetto
          </Badge>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-google">
            La <span style={{ color: penColor.hex }}>TUA</span> penna schedabusiness e' inclusa
          </h2>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: penColor.hex }}></div>
                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Pacchetto {pacchetto}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 font-google">
                Penna schedabusiness — colore {penColor.name}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Con ogni pacchetto ricevi in regalo la TUA penna schedabusiness nel colore del pacchetto scelto.
                Penna a sfera in alluminio con incisione laser del logo schedabusiness.
              </p>
              <div className="flex items-center gap-2 text-green-600 font-bold text-sm">
                <Gift className="w-4 h-4" />
                <span>INCLUSA nel prezzo — zero costi aggiuntivi</span>
              </div>
            </div>
            <div className="bg-gray-50 flex items-center justify-center p-6">
              <img
                src={penImage}
                alt={`Penna schedabusiness ${penColor.name}`}
                className="w-48 h-48 object-contain drop-shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const CrossSellingProducts = ({ pacchetto = "Plus" }) => {
  const [selectedBundle, setSelectedBundle] = useState(null);

  const handleOrder = (product) => {
    const messages = {
      penne: `Ciao Riccardo! Vorrei ordinare il pacchetto 100 penne personalizzate con il logo della MIA azienda (€199). Ho acquistato il pacchetto ${pacchetto}.`,
      stand: `Ciao Riccardo! Vorrei ordinare la base smartphone in bambu schedabusiness (€29,99). Ho acquistato il pacchetto ${pacchetto}.`,
      bundle: `Ciao Riccardo! Vorrei il BUNDLE: 100 penne personalizzate + base smartphone con il -20% (€183,19 invece di €228,99). Ho acquistato il pacchetto ${pacchetto}.`,
    };
    window.open(`${WA_LINK}?text=${encodeURIComponent(messages[product])}`, '_blank');
  };

  return (
    <section className="py-14 bg-[#faf8f5]" data-testid="cross-selling-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Badge className="bg-[#ff6b35]/10 text-[#ff6b35] border-[#ff6b35]/20 mb-3 rounded-full px-4 py-1">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Cross-selling facile facile
          </Badge>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 font-google">
            Potenzia la <span className="text-[#ff6b35]">TUA</span> presenza
          </h2>
          <p className="text-gray-500 text-sm md:text-base">
            Gadget professionali per lasciare il segno. Ordina via WhatsApp.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {/* 1: 100 Penne personalizzate */}
          <div
            className={`bg-white rounded-2xl border-2 transition-all cursor-pointer hover:shadow-lg ${
              selectedBundle === 'penne' ? 'border-[#ff6b35] shadow-lg' : 'border-gray-100'
            }`}
            onClick={() => setSelectedBundle(selectedBundle === 'penne' ? null : 'penne')}
            data-testid="cross-sell-penne"
          >
            <div className="relative overflow-hidden rounded-t-2xl bg-gray-50">
              <img
                src={ALL_PENS}
                alt="100 penne personalizzate con il TUO logo"
                className="w-full h-44 object-cover"
              />
              <div className="absolute top-2 right-2 bg-[#ff6b35] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                PERSONALIZZABILI
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Pen className="w-4 h-4 text-[#ff6b35]" />
                <h3 className="font-bold text-gray-900 text-sm">100 Penne con il TUO Logo</h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">
                Stessa qualita' delle penne schedabusiness ma con il logo della TUA azienda.
                Perfette per ristoranti, hotel, studi professionali.
              </p>
              <ul className="space-y-1.5 mb-4">
                {["Incisione laser del TUO logo", "4 colori a scelta", "Consegna in 15 giorni"].map((t, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-2xl font-black text-gray-900">€199</span>
                <span className="text-xs text-gray-400">+ IVA · 100 pezzi</span>
              </div>
              <Button
                className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] text-white rounded-full text-sm"
                onClick={(e) => { e.stopPropagation(); handleOrder('penne'); }}
                data-testid="order-penne-btn"
              >
                Ordina su WhatsApp
              </Button>
            </div>
          </div>

          {/* 2: Base Smartphone */}
          <div
            className={`bg-white rounded-2xl border-2 transition-all cursor-pointer hover:shadow-lg ${
              selectedBundle === 'stand' ? 'border-[#ff6b35] shadow-lg' : 'border-gray-100'
            }`}
            onClick={() => setSelectedBundle(selectedBundle === 'stand' ? null : 'stand')}
            data-testid="cross-sell-stand"
          >
            <div className="relative overflow-hidden rounded-t-2xl bg-gray-50">
              <img
                src={PHONE_STAND}
                alt="Base smartphone in bambu schedabusiness"
                className="w-full h-44 object-cover"
              />
              <div className="absolute top-2 right-2 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                AMULETO
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-4 h-4 text-amber-600" />
                <h3 className="font-bold text-gray-900 text-sm">Base Smartphone schedabusiness</h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">
                L'amuleto del Business Profile Manager.
                In bambu naturale con incisione schedabusiness, da tenere sulla scrivania
                del responsabile della TUA scheda Google.
              </p>
              <ul className="space-y-1.5 mb-4">
                {["Bambu naturale sostenibile", "Incisione laser logo schedabusiness", "Per smartphone e tablet"].map((t, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-2xl font-black text-gray-900">€29,99</span>
                <span className="text-xs text-gray-400">IVA inclusa</span>
              </div>
              <Button
                className="w-full bg-amber-500 hover:bg-amber-400 text-white rounded-full text-sm"
                onClick={(e) => { e.stopPropagation(); handleOrder('stand'); }}
                data-testid="order-stand-btn"
              >
                Ordina su WhatsApp
              </Button>
            </div>
          </div>

          {/* 3: BUNDLE -20% */}
          <div
            className={`bg-white rounded-2xl border-2 transition-all cursor-pointer hover:shadow-lg relative ${
              selectedBundle === 'bundle' ? 'border-green-500 shadow-lg' : 'border-gray-100'
            }`}
            onClick={() => setSelectedBundle(selectedBundle === 'bundle' ? null : 'bundle')}
            data-testid="cross-sell-bundle"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
              <div className="bg-green-500 text-white text-xs font-black px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                <Percent className="w-3.5 h-3.5" />
                RISPARMIA -20%
              </div>
            </div>
            <div className="relative overflow-hidden rounded-t-2xl bg-gray-50">
              <img
                src={BUNDLE_IMG}
                alt="Bundle penne + base smartphone schedabusiness"
                className="w-full h-44 object-cover"
              />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-green-600" />
                <h3 className="font-bold text-gray-900 text-sm">BUNDLE: Penne + Base</h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3">
                Acquistali entrambi e risparmi il 20%.
                100 penne personalizzate + base smartphone in bambu.
              </p>
              <ul className="space-y-1.5 mb-4">
                {["100 penne con il TUO logo", "Base smartphone schedabusiness", "Extra Bonus -20%"].map((t, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-black text-green-600">€183,19</span>
                <span className="text-sm text-gray-400 line-through">€228,99</span>
              </div>
              <Button
                className="w-full bg-green-600 hover:bg-green-500 text-white rounded-full text-sm"
                onClick={(e) => { e.stopPropagation(); handleOrder('bundle'); }}
                data-testid="order-bundle-btn"
              >
                Ordina Bundle su WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
